// src/asset-delivery/components/PrivateCdnImage.tsx

"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import baseApi from "@/lib/baseApi";

import "./PrivateCdnImage.css";

export type PrivateCdnTarget = {
  app_label: string;
  model: string;
  object_id: number;
};

export type PrivateCdnKind = "image" | "thumbnail";

/* -------------------------------------------------
 * Module cache
 * ------------------------------------------------- */
// Cache warmups by object identity to avoid duplicate requests.
// Failed warmups are removed so future attempts can retry.
const warmupCache = new Map<string, Promise<void>>();

/* -------------------------------------------------
 * Helpers
 * ------------------------------------------------- */
function isLocalLike(url?: string | null) {
  if (!url) return false;
  const u = String(url).trim();
  return u.startsWith("/") || u.startsWith("data:") || u.startsWith("blob:");
}

function buildWarmupKey(
  target: PrivateCdnTarget,
  kind: PrivateCdnKind,
  fieldName: string
) {
  return `${kind}:${fieldName}:${target.app_label}:${target.model}:${target.object_id}`;
}

function warmupEndpoint(kind: PrivateCdnKind) {
  return kind === "thumbnail"
    ? "/assets/playback/thumbnail/"
    : "/assets/playback/image/";
}

async function warmCookiesOnce(opts: {
  target: PrivateCdnTarget;
  kind: PrivateCdnKind;
  fieldName: string;
  intent?: "preload" | "play";
  withCredentials?: boolean;
}) {
  const {
    target,
    kind,
    fieldName,
    intent = "preload",
    withCredentials = true,
  } = opts;

  // Sets CloudFront cookies for this object.
  await baseApi.get(warmupEndpoint(kind), {
    params: { ...target, field_name: fieldName, intent },
    withCredentials,
  });
}

/* -------------------------------------------------
 * Props
 * ------------------------------------------------- */
export type PrivateCdnImageProps = {
  /** Clean CDN URL without signed query. */
  src?: string | null;

  /** Optional placeholder layer. */
  placeholderSrc?: string | null;

  /** Required identity for backend cookie warmup. */
  target: PrivateCdnTarget;

  /** Playback endpoint type. */
  kind: PrivateCdnKind;

  /** Backend field name to resolve. */
  fieldName: string;

  /** Render */
  alt?: string;

  /**
   * Backward-compatible:
   * - className applies to WRAPPER
   * - imgClassName applies to IMG
   */
  className?: string;
  imgClassName?: string;

  /** Behavior */
  eager?: boolean;
  rootMargin?: string;
  threshold?: number;

  /** Skip warmup for public/local images. */
  skipWarmup?: boolean;

  /** Allow image render if warmup finally fails. */
  failOpen?: boolean;

  /** Debug only */
  debugKey?: string;

  /** Image loading strategy */
  imgLoading?: "eager" | "lazy";
  fetchPriority?: "high" | "low" | "auto";

  /** Controlled retry after warmup/image failure. */
  maxRetries?: number;
  retryDelayMs?: number;

  /** Events */
  onLoad?: () => void;
  onError?: () => void;
};

/* -------------------------------------------------
 * Component
 * ------------------------------------------------- */
const PrivateCdnImage: React.FC<PrivateCdnImageProps> = ({
  src,
  placeholderSrc,
  target,
  kind,
  fieldName,
  alt = "",
  className,
  imgClassName,
  eager = false,
  rootMargin = "400px",
  threshold = 0.01,
  skipWarmup = false,
  failOpen = true,
  debugKey,
  imgLoading = "lazy",
  fetchPriority = "auto",
  maxRetries = 2,
  retryDelayMs = 350,
  onLoad,
  onError,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isNear, setIsNear] = useState<boolean>(Boolean(eager));
  const [cookiesReady, setCookiesReady] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState<number>(0);

  const finalSrc = (src || "").trim();
  const rawPlaceholder = (placeholderSrc || "").trim();

  // Avoid loading the same URL twice.
  const placeholder =
    rawPlaceholder && rawPlaceholder !== finalSrc ? rawPlaceholder : "";

  // Auto-skip warmup for local-like sources.
  const effectiveSkipWarmup = useMemo(() => {
    if (skipWarmup) return true;
    if (isLocalLike(finalSrc)) return true;
    if (!finalSrc && isLocalLike(placeholder)) return true;
    return false;
  }, [skipWarmup, finalSrc, placeholder]);

  const warmKey = useMemo(
    () => buildWarmupKey(target, kind, fieldName),
    [target.app_label, target.model, target.object_id, kind, fieldName]
  );

  // Reset visual/retry state only when real inputs change.
  const identityKey = useMemo(() => {
    return `${warmKey}:${finalSrc || "no-src"}:${placeholder || "no-ph"}:${
      effectiveSkipWarmup ? "skip" : "warm"
    }`;
  }, [warmKey, finalSrc, placeholder, effectiveSkipWarmup]);

  const clearRetryTimer = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const scheduleRetry = (reason: "warmup" | "image") => {
    if (retryCount >= maxRetries) return false;

    clearRetryTimer();

    // Force a fresh cookie warmup before the next image attempt.
    warmupCache.delete(warmKey);
    setLoaded(false);
    setFailed(false);
    setCookiesReady(Boolean(effectiveSkipWarmup));

    retryTimerRef.current = setTimeout(() => {
      setRetryCount((current) => current + 1);
    }, retryDelayMs);

    if (debugKey) {
      // eslint-disable-next-line no-console
      console.warn(
        "[PrivateCdnImage] retry scheduled:",
        debugKey,
        reason,
        `${retryCount + 1}/${maxRetries}`,
        warmKey
      );
    }

    return true;
  };

  useEffect(() => {
    clearRetryTimer();

    setLoaded(false);
    setFailed(false);
    setRetryCount(0);
    setCookiesReady(Boolean(effectiveSkipWarmup));
  }, [identityKey, effectiveSkipWarmup]);

  useEffect(() => {
    return () => {
      clearRetryTimer();
    };
  }, []);

  /* -------------------------------------------------
   * 1) Near-viewport detection
   * ------------------------------------------------- */
  useEffect(() => {
    if (eager) {
      setIsNear(true);
      return;
    }

    const el = rootRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsNear(true);
          io.disconnect();
        }
      },
      { rootMargin, threshold }
    );

    io.observe(el);

    return () => {
      io.disconnect();
    };
  }, [eager, rootMargin, threshold]);

  /* -------------------------------------------------
   * 2) Cookie warmup
   * ------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;

    if (effectiveSkipWarmup) return;
    if (!isNear) return;
    if (!finalSrc) return;
    if (cookiesReady) return;

    if (!warmupCache.has(warmKey)) {
      const p = warmCookiesOnce({
        target,
        kind,
        fieldName,
        intent: "preload",
        withCredentials: true,
      }).catch((err) => {
        // Remove failed promise so future attempts can retry.
        warmupCache.delete(warmKey);
        throw err;
      });

      warmupCache.set(warmKey, p);
    }

    warmupCache
      .get(warmKey)!
      .then(() => {
        if (cancelled) return;

        setFailed(false);
        setCookiesReady(true);

        if (debugKey) {
          // eslint-disable-next-line no-console
          console.log(
            "[PrivateCdnImage] cookiesReady:",
            debugKey,
            warmKey,
            `retry=${retryCount}`
          );
        }
      })
      .catch(() => {
        if (cancelled) return;

        const retrying = scheduleRetry("warmup");

        if (retrying) return;

        // After final warmup failure, fail open if requested.
        setFailed(true);
        setCookiesReady(Boolean(failOpen));

        if (debugKey) {
          // eslint-disable-next-line no-console
          console.warn(
            "[PrivateCdnImage] warmup failed:",
            debugKey,
            warmKey,
            `retry=${retryCount}`
          );
        }

        if (!failOpen) {
          onError?.();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    effectiveSkipWarmup,
    isNear,
    finalSrc,
    cookiesReady,
    warmKey,
    target,
    kind,
    fieldName,
    failOpen,
    debugKey,
    retryCount,
  ]);

  /* -------------------------------------------------
   * Render guards
   * ------------------------------------------------- */
  if (!finalSrc && !placeholder) {
    return (
      <div
        ref={rootRef}
        className={`tl-private-cdn-image empty ${className || ""}`}
        aria-hidden
      />
    );
  }

  const mainUrl = finalSrc || placeholder;

  return (
    <div
      ref={rootRef}
      className={[
        "tl-private-cdn-image",
        loaded ? "is-loaded" : "is-loading",
        failed ? "is-failed" : "",
        className || "",
      ].join(" ")}
    >
      {/* Optional placeholder layer */}
      {placeholder && !loaded && (
        <img
          src={placeholder}
          alt=""
          aria-hidden
          className="tl-private-cdn-image__placeholder"
          loading="lazy"
          decoding="async"
          draggable={false}
        />
      )}

      {/* Main image waits for cookies unless warmup is skipped. */}
      {cookiesReady ? (
        <img
          src={mainUrl}
          alt={alt}
          className={["tl-private-cdn-image__main", imgClassName || ""]
            .join(" ")
            .trim()}
          loading={imgLoading}
          fetchPriority={fetchPriority as any}
          decoding="async"
          draggable={false}
          onLoad={() => {
            clearRetryTimer();
            setLoaded(true);
            setFailed(false);
            onLoad?.();
          }}
          onError={() => {
            setLoaded(false);

            if (!effectiveSkipWarmup) {
              const retrying = scheduleRetry("image");
              if (retrying) return;
            }

            setFailed(true);
            onError?.();

            if (debugKey) {
              // eslint-disable-next-line no-console
              console.warn(
                "[PrivateCdnImage] image failed:",
                debugKey,
                mainUrl,
                `retry=${retryCount}`
              );
            }
          }}
        />
      ) : (
        <div className="tl-private-cdn-image__skeleton" aria-hidden />
      )}
    </div>
  );
};

export default PrivateCdnImage;